/* eslint react/jsx-handler-names: "off" */
import React, {useEffect, useMemo, useState} from 'react';
import {Zoom} from '@visx/zoom';
import {localPoint} from '@visx/event';
import {generateTaskDAGData} from "../../../../utils/cpm-functions/set-cpm-map/functions/generateDAGData";
import * as d3Dag from "d3-dag";
import {Group} from "@visx/group";
import {useActiveProjectCalendarsSelector, useCpmMapSelector} from "../../../../store/selectors/project.selectors";
import {LinkHorizontal} from "@visx/shape";
import {Popup, PopupContent, PopupHeader,} from 'semantic-ui-react'
import {convertIndexToSeconds} from "../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {useDispatch} from "react-redux";
import * as taskActions from "../../../../store/actions/task.actions";
import {useAllTaskListSelector} from "../../../../store/selectors/task/task.selectors";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faGear, faCrown, faExpand, faCompress, faDiamond} from "@fortawesome/pro-solid-svg-icons";
import {useProjectMemberListSelector} from "../../../../store/selectors/team.selectors";
import ProjectMembersModel from "../../../../models/responses/project-members.model";
import UserImage from "../../../../images/UserImage";
import {MemberOrganizationEnum} from "../../../../models/member-organization.enum";
import {TaskType} from "../../../../models/task-type";
import TaskStatusModel from "../../../../models/responses/task-status.model";

const blue = "#4285f4";
const predStatusRed = "#d9001b";
const predStatusGreen = "#0f9d58";
const predStatusYellow = "#f4b400";
const yellow = "#f1e05a";
const lightpurple = "#374469";
const bg = '#CFE7D8';
const redBg = "#F6D1CE";
const background = "white"
const selected = 'rgba(203, 230, 255)'
const hoverColour = 'rgba(241, 248, 255)'

const nodeWidth = 180;
const nodeHeight = 60;

export type ZoomIProps = {
    width: number;
    initialHeight: number;
    taskId: string | null;
    delay: boolean
    scope: string
    setShowDag: (show: boolean) => void
    setActiveGreenMilestone: (milestone: any) => void
    setActiveRedMilestone: (milestone: any) => void
    driving: boolean,
    navigateToTask: any,
    setDagLoaded: (loaded: boolean) => void
};

const handleStatusTypes = {
    [TaskStatusModel.COMPLETE]: 'completed',
    [TaskStatusModel.IN_PROGRESS]: 'in progress',
    [TaskStatusModel.NOT_STARTED]: 'not started',
    [TaskStatusModel.SUSPENDED]: 'in progress',
    [TaskStatusModel.BLOCK]: 'not started',
    [TaskStatusModel.DECLARED_COMPLETE]: 'completed',
}
export default function NetworkDag({ width, initialHeight, taskId, delay, scope, navigateToTask, setDagLoaded}: ZoomIProps) {
    const [dag, setDag] = useState<any | null>(null);
    const [height, setHeight] = useState(initialHeight);
    const [layoutWidth, setLayoutWidth] = useState(0);
    const [layoutHeight, setLayoutHeight] = useState(0);
    const cpmMap = useCpmMapSelector()
    const projectCalendars = useActiveProjectCalendarsSelector()
    const dispatch = useDispatch()
    const [drivers, setDrivers] = useState<any>(null)
    const allTasks = useAllTaskListSelector();
    const projectMembers = useProjectMemberListSelector();
    const [translation, setTranslation] = useState({x: 0, y: 0})
    const zoomRef = React.useRef<any>(null);
    const [hoverTask, setHoverTask] = useState<string | null>(taskId)
    const [hoverLink, setHoverLink] = useState<number | null>(null)
    const [hoverUser, setHoverUser] = useState<{email: string, x: number, y: number} | null>(null)
    const [fullScreen, setFullScreen] = useState(false)

    const projectMemberMap = useMemo(() => {
        let memberMap = new Map<string, ProjectMembersModel>();
        projectMembers.forEach(member => {
            memberMap.set(member.userId, member);
            memberMap.set(member.userEmail, member);
        });
        return memberMap;
    }, [projectMembers]);

    function getDateFromIndex(taskId: string) {
        const task = cpmMap.get(taskId)
        const date = convertIndexToSeconds(task?.ef, task?.cal_id, projectCalendars)
        if (task) {
            return new Date(date * 1000).toDateString()
        }
        return new Date().toDateString()
    }

    function centerOnSelectedNode(zoom: any, selectedNode: any) {
        if (!selectedNode) return;

        const { scaleX, scaleY } = zoom.transformMatrix;

        const centerX = width / 2;
        const centerY = height / 2;

        const newTranslateX = centerX - selectedNode.uy * scaleX;
        const newTranslateY = centerY - selectedNode.ux * scaleY;

        zoom.setTransformMatrix({
            scaleX,
            scaleY,
            translateX: newTranslateX,
            translateY: newTranslateY,
            skewX: 0,
            skewY: 0,
        });
    }

    useEffect(() => {
        const {dagData, drivingTasks} = generateTaskDAGData(taskId, cpmMap)
        const dag = d3Dag.graphStratify()(dagData);
        const nodeRadius = 30; //need to change based on the nodeWidth value
        const layout = d3Dag
            .sugiyama() // base layout
            .layering(d3Dag.layeringLongestPath())
            .decross(d3Dag.decrossTwoLayer()) // minimize number of crossings
            .nodeSize((node) => [(node ? 3.8 : 0.25) * nodeRadius, 8 * nodeRadius]); // set node size instead of constraining to fit
        const { width, height } = layout(dag as any);
        setDrivers(drivingTasks);
        setDag(dag);
        setLayoutHeight(width);
        setLayoutWidth(height);
    }, [taskId, width]);

    const links = useMemo(() => {
        if (!dag) return [];
        let outLinks: any[] = []
        for (const link of dag.links()) {
            outLinks.push(link);
        }
        return outLinks
    }, [dag]);

    const nodes = useMemo(() => {
        if (!dag) return [];
        let outNodes: any[] = []
        for (const node of dag.nodes()) {
            outNodes.push(node);
        }
        return outNodes
    }, [dag]);

    useEffect(() => {
        if (!dag) return;
        if (fullScreen) {
            setHeight(window.innerHeight * 0.7);
        } else {
            setHeight(initialHeight);
        }
    }, [fullScreen]);

    const initialTransform = useMemo(() => {
        if (!dag) return {
            scaleX: 1.0,
            scaleY: 1.0,
            translateX: 0,
            translateY: 0,
            skewX: 0,
            skewY: 0,
        }
        const centerX = width / 2;
        const centerY = height / 2;
        const selectedNode = dag.nodes().find((node: any) => node.data.id === taskId);
        const newTranslateX = centerX - selectedNode.uy
        const newTranslateY = centerY - selectedNode.ux

        return {
            scaleX: 1,
            scaleY: 1,
            translateX: newTranslateX,
            translateY: newTranslateY,
            skewX: 0,
            skewY: 0,
        }
        }, [dag])

    const hoverTaskData = useMemo(() => {
        if (hoverTask) {
            return allTasks.get(hoverTask)
        } else {
            return null
        }
    }, [hoverTask, allTasks])

    // use effect to centre plot after node change
    useEffect(() => {
        if (!taskId || !nodes.length || !zoomRef.current) return;

        const selectedNode = nodes.find(node => node.data.id === taskId);
        if (selectedNode) {
            centerOnSelectedNode(zoomRef.current, selectedNode);
        }

        setDagLoaded(true)
    }, [dag])

    return dag ? (
        <Zoom<SVGSVGElement>
            width={layoutWidth}
            height={layoutHeight}
            scaleXMin={1 / 4}
            scaleXMax={4}
            scaleYMin={1 / 4}
            scaleYMax={4}
            initialTransformMatrix={initialTransform}
        >
            {(zoom) => {
                zoomRef.current = zoom;
                return (
                <div className="relative" onMouseLeave={() => setHoverTask(null)}>
                    <svg
                        width={width}
                        height={height}
                        style={{cursor: zoom.isDragging ? 'grabbing' : 'auto', touchAction: 'none'}}
                        ref={zoom.containerRef}
                    >
                        <defs>
                            <marker
                                id="arrow"
                                viewBox="0 -5 10 10"
                                refX="15"
                                refY="-.5"
                                markerWidth="9"
                                markerHeight="9"
                                orient="auto"
                                fill={lightpurple}
                            >
                                <path d="M0,-5L10,0L0,5"/>
                            </marker>
                            <filter id="f4" width="120" height="120">
                                <feOffset in="SourceGraphic" dx="3" dy="3" />
                                <feColorMatrix type="matrix" values = "0.2 0 0 0 0 0 0.2 0 0 0 0 0 0.2 0 0 0 0 0 1 0"/>
                                <feGaussianBlur stdDeviation="2" />
                                <feBlend in="SourceGraphic" in2="blurOut" />
                            </filter>
                        </defs>
                        <rect width={width} height={height} fill={scope === "preds" ? "white" : delay ? redBg : bg}/>
                        <rect
                            width={width}
                            height={height}
                            fill="transparent"
                            onTouchStart={zoom.dragStart}
                            onTouchMove={zoom.dragMove}
                            onTouchEnd={zoom.dragEnd}
                            onMouseDown={zoom.dragStart}
                            onMouseMove={zoom.dragMove}
                            onMouseUp={zoom.dragEnd}
                            onMouseLeave={() => {
                                if (zoom.isDragging) zoom.dragEnd();
                            }}
                            onDoubleClick={(event) => {
                                const point = localPoint(event) || {x: 0, y: 0};
                                zoom.scale({scaleX: 1.1, scaleY: 1.1, point});
                            }}
                        />
                        <Group transform={zoom.toString()}>
                            {links.map((link, i) => {
                                const predTask = cpmMap.get(link.source.data.id);
                                const succTask = cpmMap.get(link.target.data.id);
                                return (
                                <LinkHorizontal
                                    key={`link-${i}`}
                                    data={link}
                                    stroke={lightpurple}
                                    strokeWidth={hoverLink === i ? '2' : '1'}
                                    strokeDasharray={handleStatusTypes[predTask.status_code] === TaskStatusModel.COMPLETE ||
                                        handleStatusTypes[succTask.status_code] === TaskStatusModel.COMPLETE ? '5, 5' :
                                        'none'}
                                    fill="none"
                                    x={(node: any) => node.y - nodeWidth / 2 + 5}
                                    markerEnd="url(#arrow)"
                                    onMouseOver={() => setHoverLink(i)}
                                    // onMouseOver={() => console.log("Link clicked", link)}
                                    // onMouseLeave={() => setHoverLink(null)}
                                />)}
                            )}
                            {nodes.map((node, i) => {
                                const task = cpmMap.get(node.data.id)
                                return (
                                    <Popup
                                        key={`node-${i}`}
                                        open={hoverTask === node.data.id}
                                        onMouseLeave={() => setHoverTask(null)}
                                        trigger={
                                            <Group
                                                top={node.ux}
                                                left={node.uy}
                                                style={{cursor: "pointer"}}
                                                onClick={() => {
                                                    const activeTaskInput = allTasks.get(node.data.id) || null;
                                                    setTranslation({x: zoom.transformMatrix.translateX, y: zoom.transformMatrix.translateY});
                                                    dispatch(taskActions.Actions.setActiveTask(activeTaskInput))
                                                    navigateToTask(activeTaskInput)
                                                    centerOnSelectedNode(zoom, node)
                                                }}
                                                onMouseOver={() => setHoverTask(node.data.id)}
                                                // onMouseOut={() => setHoverTask(null)}
                                            >
                                                <rect
                                                    height={nodeHeight}
                                                    width={nodeWidth}
                                                    y={-nodeHeight / 2}
                                                    x={-nodeWidth / 2}
                                                    fill={node.data.id === taskId ? selected :
                                                        hoverTask === node.data.id ? hoverColour :
                                                        background}
                                                    opacity={1}
                                                    stroke={task?.predStatus === 1 ? predStatusGreen :
                                                        task?.predStatus === 2 ? predStatusYellow :
                                                            task?.predStatus === 3 ? predStatusRed : blue}
                                                    strokeWidth={1}
                                                    rx={5}
                                                    filter={"url(#f4)"}
                                                />
                                                <path
                                                    d={`M-${nodeWidth / 2 - 5},-${nodeHeight / 2 - 1}
                                                       h15
                                                       v${nodeHeight - 2}
                                                       h-15
                                                       q-5,0 -5,-5
                                                       v-${nodeHeight - 12}
                                                       q0,-5 5,-5
                                                       z
                                                    `}
                                                    fill={task?.predStatus === 1 ? predStatusGreen :
                                                        task?.predStatus === 2 ? predStatusYellow :
                                                            task?.predStatus === 3 ? predStatusRed : blue}
                                                    opacity={1}
                                                    stroke={task?.predStatus === 1 ? predStatusGreen :
                                                        task?.predStatus === 2 ? predStatusYellow :
                                                            task?.predStatus === 3 ? predStatusRed : blue}
                                                    strokeWidth={2}
                                                    // filter={"url(#f4)"}
                                                />
                                                {task.task_type === TaskType.TT_MILE || task.task_type === TaskType.TT_FIN_MILE ?
                                                    // draw diamond shape for milestones
                                                    <path
                                                    d={
                                                        `M-${nodeWidth / 2 - 10},-11 L-${nodeWidth / 2 - 20},-1
                                                        L-${nodeWidth / 2 - 10},9 L-${nodeWidth / 2},-1 Z
                                                        `}
                                                    stroke="white"
                                                    fill="white"
                                                    strokeWidth={1}
                                                    fillOpacity={0.5}
                                                /> : null}
                                                {drivers && drivers.get(node.data.id) && <foreignObject
                                                    width={20}
                                                    height={20}
                                                    x={-nodeWidth / 2 + 3}
                                                    y={-nodeHeight / 6}
                                                    color={'white'}
                                                >
                                                    <FontAwesomeIcon icon={faGear}/>
                                                </foreignObject>}
                                                <foreignObject
                                                    width={nodeWidth - 20}
                                                    height={nodeHeight}
                                                    x={-nodeWidth / 2 + 20}
                                                    y={-nodeHeight / 2}
                                                >
                                                    <div style={
                                                        {
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                            height: nodeHeight,
                                                            width: nodeWidth - 20,
                                                            overflow: "hidden",
                                                            pointerEvents: "none",
                                                            gap: "5px",
                                                        }
                                                    }>
                                                        <p style={{
                                                            pointerEvents: "none",
                                                            textAlign: "center",
                                                            fontSize: 14,
                                                        }}>
                                                            {task?.p6_id}
                                                        </p>
                                                    </div>
                                                </foreignObject>
                                            </Group>
                                        }>
                                        {hoverTaskData && <PopupHeader>{hoverTaskData.wbs}</PopupHeader>}
                                        {hoverTaskData && <PopupContent>
                                            {hoverTaskData.task_name}
                                            {hoverTaskData.taskOwner && <div style={{
                                                background: '#a6a6a6',
                                                    color: '#fff',
                                                    fontSize: '12px',
                                                    padding: '1px 5px',
                                                    maxHeight: '20px',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    marginBottom: '2px',
                                                }}
                                            ><FontAwesomeIcon icon={faCrown}
                                                              style={{
                                                                marginRight: '5px',
                                                                color: '#fff',
                                                                fontSize: '12px',
                                                              }}
                                            />{projectMemberMap.get(hoverTaskData.taskOwner)?.userEmail || hoverTaskData.taskOwner}</div>}
                                            {hoverTaskData.suspendReason || hoverTaskData.overdueReason ?
                                                <div style={{
                                                    background: '#333333',
                                                    color: '#fff',
                                                    fontSize: '12px',
                                                    padding: '1px 5px',
                                                    maxHeight: '20px',
                                                    textAlign: "center",
                                                    justifyContent: "center",
                                                    display: "flex",
                                                }}>{hoverTaskData.suspendReason || hoverTaskData.overdueReason}</div>
                                                : null}
                                            <div style={{display: "grid", gridTemplateColumns: "repeat(8, 26px)", marginTop: "5px"}}>
                                            {hoverTaskData.taskForce.map(user => {
                                                const projectMember = projectMemberMap.get(user)
                                                if (projectMember && projectMember.grade !== MemberOrganizationEnum.GROUPS) {
                                                    return (<div
                                                        key={user.userId}
                                                        onMouseOver={(e) => {
                                                            setHoverUser({
                                                                email: projectMember?.userEmail,
                                                                x: e.clientX,
                                                                y: e.clientY,
                                                            })}}
                                                        onMouseLeave={() => setHoverUser(null)}>
                                                        <UserImage
                                                            user={projectMember}
                                                            key={user}
                                                            width={25}
                                                            height={25}
                                                        /></div>)
                                                }
                                                return null
                                            })}
                                            </div>
                                        </PopupContent>}
                                    </Popup>
                                )
                            })}
                        </Group>
                    </svg>
                    <div className="controls">
                        <button
                            type="button"
                            className="btn btn-zoom"
                            onClick={() => zoom.scale({scaleX: 1.2, scaleY: 1.2})}
                        >
                            +
                        </button>
                        <button
                            type="button"
                            className="btn btn-zoom btn-bottom"
                            onClick={() => zoom.scale({scaleX: 0.8, scaleY: 0.8})}
                        >
                            -
                        </button>
                        {/*<button type="button" className="btn btn-lg" onClick={zoom.center}>*/}
                        {/*    Center*/}
                        {/*</button>*/}
                        <button type="button" className="btn btn-lg" onClick={zoom.reset}>
                            Reset
                        </button>
                        <button style={{position: "absolute", top: `${height - 40}px`}} type="button" className="btn btn-lg" onClick={() => {
                            setHoverTask(fullScreen ? null : taskId)
                            const currentTransform = zoom.transformMatrix
                            if (fullScreen) {
                                zoom.setTransformMatrix({
                                    scaleX: currentTransform.scaleX,
                                    scaleY: currentTransform.scaleY,
                                    translateX: currentTransform.translateX,
                                    // translateY: currentTransform.translateY - ((window.innerHeight * 0.7) - initialHeight),
                                    translateY: currentTransform.translateY + (initialTransform.translateY - currentTransform.translateY),
                                    skewX: 0,
                                    skewY: 0,
                                })
                            }
                            setFullScreen(!fullScreen)
                        }}>
                            {fullScreen ? <FontAwesomeIcon icon={faCompress} size={'lg'} /> : <FontAwesomeIcon icon={faExpand} size={'lg'} />}
                        </button>
                    </div>
                    {hoverUser && <div style={{
                        background: '#333333',
                        color: '#fff',
                        fontSize: '12px',
                        padding: '1px 5px',
                        maxHeight: '20px',
                        overflow: 'hidden',
                        position: 'fixed',
                        top: `${hoverUser.y + 35}px`,
                        left: `${hoverUser.x}px`,
                        zIndex: 1500,
                    }}>{hoverUser.email}</div>}
                </div>
            )}}
        </Zoom>
    ) : null;
}
